import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ActionBar,
	AddIcon,
	Dialog,
	Modal,
	ModuleHeader,
	RolesIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { RoleForm } from './components/RoleForm'
import type { Role, RoleFormInput, RoleListInput } from './types'

export const RolesPage = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const queryClient = useQueryClient()
	const toast = useToast()
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
	const [editingRole, setEditingRole] = useState<Role | null>(null)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

	const { data: organization, isLoading: isLoadingOrg } = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})
	const createMutation = useMutation({
		...trpc.admin.roles.create.mutationOptions(),
		onSuccess: () => {
			setIsCreateModalOpen(false)
			toast.showToast('Role created')
		},
		onError: (error) => {
			toast.showToast(error.message || 'An error occurred. Try again later.', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.roles.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.roles.list.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const editMutation = useMutation({
		...trpc.admin.roles.edit.mutationOptions(),
		onSuccess: () => {
			setEditingRole(null)
			toast.showToast('Role updated')
		},
		onError: (error) => {
			toast.showToast(error.message || 'An error occurred. Try again later.', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.roles.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.roles.list.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const deleteMutation = useMutation({
		...trpc.admin.roles.delete.mutationOptions(),
		onSuccess: () => {
			setSelectedIds(new Set())
			toast.showToast('Role deleted')
		},
		onError: (error) => {
			toast.showToast(error.message || 'An error occurred. Try again later.', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.roles.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.roles.list.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const columns: Column<Role>[] = [
		{
			field: 'name',
			label: 'Role Name',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.name}
				</Text>
			),
		},
		{
			field: 'displayName',
			label: 'Display Name',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.displayName}
				</Text>
			),
		},
		{
			field: 'description',
			label: 'Description',
			colSpan: 2,
			render: (role: Role) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{role.description}
				</Text>
			),
		},
	]

	const handleCreateRole = (data: RoleFormInput) => {
		if (!organizationId) return
		createMutation.mutate({
			...data,
			organizationId,
		})
	}

	const handleUpdateRole = (data: RoleFormInput) => {
		if (!editingRole?.id || !organizationId) return
		editMutation.mutate({
			id: editingRole.id,
			organizationId,
			...data,
		})
	}

	const handleDeleteRoles = async () => {
		const selectedRoleIds = Array.from(selectedIds)
		try {
			await Promise.all(
				selectedRoleIds.map((id) => deleteMutation.mutateAsync({ id, organizationId: organizationId as string })),
			)
		} finally {
			setIsDeleteModalOpen(false)
		}
	}

	const handleRowClick = (role: Role) => {
		setEditingRole(role)
	}

	const handleSelectionChange = (items: Role[]) => {
		const newIds = new Set(items.map((item) => item.id))
		if (newIds.size !== selectedIds.size || ![...newIds].every((id) => selectedIds.has(id))) {
			setSelectedIds(newIds)
		}
	}

	if (isLoadingOrg) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Organizations', to: '/organizations' },
					{ label: organization?.name || '', to: `/organizations/${organizationId}` },
					{ label: 'Form Roles' },
				]}
				title='Form Roles'
				logo={{
					url: organization?.logo || '',
					name: organization?.name || '',
				}}
			/>
			<GenericTable<Role, RoleListInput>
				columns={columns}
				idField='id'
				onRowClick={handleRowClick}
				selectable
				onSelectionChange={handleSelectionChange}
				queryOptionsGenerator={(options) => ({
					...trpc.admin.roles.search.queryOptions({
						...options,
						organizationId: organizationId as string,
					}),
				})}
				showSearch
				showPagination
				actionButtons={[
					{
						children: 'Add new role',
						icon: AddIcon,
						iconPosition: 'left',
						onClick: () => {
							setIsCreateModalOpen(true)
							setEditingRole(null)
						},
						variant: 'primary' as const,
					},
				]}
				emptyState={{
					icon: RolesIcon,
					title: 'No roles created yet',
					description: 'Get started by adding a new role',
					actionLabel: 'Add new role',
					onAction: () => {
						setIsCreateModalOpen(true)
						setEditingRole(null)
					},
				}}
			/>

			{(isCreateModalOpen || editingRole) && (
				<Modal
					isOpen
					onClose={() => {
						setIsCreateModalOpen(false)
						setEditingRole(null)
					}}
					title={editingRole ? 'Edit role' : 'Add new role'}
					buttons={[
						{
							variant: 'secondary',
							children: 'Cancel',
							onClick: () => {
								setIsCreateModalOpen(false)
								setEditingRole(null)
							},
						},
						{
							variant: 'primary',
							isLoading: createMutation.isPending || editMutation.isPending,
							children: editingRole ? 'Save changes' : 'Add role',
							onClick: () => {
								const form = document.getElementById(editingRole ? 'editRoleForm' : 'addRoleForm') as HTMLFormElement
								form?.dispatchEvent(new Event('submit', { bubbles: true }))
							},
						},
					]}>
					<RoleForm
						initialValues={{
							name: editingRole?.name ?? '',
							displayName: editingRole?.displayName ?? '',
							description: editingRole?.description ?? '',
						}}
						onSubmit={editingRole ? handleUpdateRole : handleCreateRole}
						isSubmitting={createMutation.isPending || editMutation.isPending}
						showButtons={false}
					/>
				</Modal>
			)}

			{selectedIds.size > 0 && (
				<ActionBar
					rightActions={[
						{
							label: `Delete roles (${selectedIds.size})`,
							onClick: () => setIsDeleteModalOpen(true),
							variant: 'destructive',
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				onConfirm={handleDeleteRoles}
				title='Delete roles'
				message={`Are you sure you want to delete ${selectedIds.size} ${selectedIds.size === 1 ? 'role' : 'roles'}? This action cannot be reversed.`}
				type='delete'
				confirmLabel='Delete'
			/>
		</main>
	)
}
